// ============================================================
// Braval - Edit Auction Page
// ============================================================

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { apiFetch } from '../api';
import { Header } from '../App';
import AuctionForm from './AuctionForm';

// ------------------------------------------------------------
// Excel import helpers
// ------------------------------------------------------------

const HEADER_MAP = {
    'lot': 'lot_number', 'lot #': 'lot_number', 'lot#': 'lot_number',
    'lot number': 'lot_number', 'lot_number': 'lot_number',
    'title': 'title', 'item title': 'title', 'item name': 'title',
    'description': 'description', 'desc': 'description', 'item description': 'description',
    'make': 'make', 'manufacturer': 'make', 'brand': 'make',
    'model': 'model', 'model number': 'model', 'model #': 'model',
    'year': 'year', 'yr': 'year', 'manufacture year': 'year', 'model year': 'year',
    'serial': 'serial_number', 'serial number': 'serial_number', 'serial #': 'serial_number',
    's/n': 'serial_number', 'sn': 'serial_number', 'serial_number': 'serial_number',
    'price': 'sale_price', 'sale price': 'sale_price', 'sale_price': 'sale_price',
    'sold': 'sale_price', 'hammer price': 'sale_price', 'hammer': 'sale_price',
    'bids': 'number_of_bids', '# bids': 'number_of_bids', 'number of bids': 'number_of_bids',
    'number_of_bids': 'number_of_bids', 'bid count': 'number_of_bids',
};

function parseExcelFile(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const workbook = XLSX.read(e.target.result, { type: 'array' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
                resolve(rawRows);
            } catch (err) {
                reject(err);
            }
        };
        reader.onerror = () => reject(new Error('Could not read file.'));
        reader.readAsArrayBuffer(file);
    });
}

function mapHeaders(rawRows) {
    if (!rawRows.length) return { rows: [], unrecognised: [] };

    const unrecognised = [];
    for (const key of Object.keys(rawRows[0])) {
        if (!HEADER_MAP[key.toLowerCase().trim()]) unrecognised.push(key);
    }

    const rows = rawRows.map((raw, i) => {
        const row = { _rowIndex: i + 1 };
        for (const [key, value] of Object.entries(raw)) {
            const mapped = HEADER_MAP[key.toLowerCase().trim()];
            if (mapped) row[mapped] = value === '' ? null : value;
        }
        row._error = validateRow(row);
        return row;
    });

    return { rows, unrecognised };
}

function validateRow(row) {
    if (row.year !== null && row.year !== undefined && row.year !== '') {
        const y = parseInt(row.year);
        if (isNaN(y) || y < 1900 || y > 2100) return `Invalid year: "${row.year}"`;
    }
    if (row.sale_price !== null && row.sale_price !== undefined && row.sale_price !== '') {
        const p = parseFloat(String(row.sale_price).replace(/[$,]/g, ''));
        if (isNaN(p) || p < 0) return `Invalid price: "${row.sale_price}"`;
    }
    if (row.number_of_bids !== null && row.number_of_bids !== undefined && row.number_of_bids !== '') {
        const b = parseInt(row.number_of_bids);
        if (isNaN(b) || b < 0) return `Invalid bids: "${row.number_of_bids}"`;
    }
    return null;
}

function downloadTemplate() {
    const csv = [
        'lot_number,title,description,make,model,year,serial_number,sale_price,number_of_bids',
        '101,Example Item,Item description here,CAT,320,2019,SN12345,87500,14',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'lot-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
}

// ------------------------------------------------------------
// Lot Import Panel
// ------------------------------------------------------------
function LotImportPanel({ projectId, rows, unrecognised, onDone, onCancel }) {
    const [importing, setImporting]     = useState(false);
    const [result, setResult]           = useState(null);
    const [importError, setImportError] = useState(null);
    const [showAll, setShowAll]         = useState(false);

    const validCount  = rows.filter(r => !r._error).length;
    const errorCount  = rows.filter(r =>  r._error).length;
    const displayRows = showAll ? rows : rows.slice(0, 10);

    const handleImport = async () => {
        setImporting(true);
        setImportError(null);
        try {
            const payload = rows.map(({ _rowIndex, _error, ...rest }) => rest);
            const res  = await apiFetch(`/api/auctions/${projectId}/lots/import`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ lots: payload }),
            });
            const data = await res.json();
            if (!res.ok) {
                setImportError(data.error || 'Import failed.');
            } else {
                setResult(data);
            }
        } catch {
            setImportError('Could not reach the API.');
        } finally {
            setImporting(false);
        }
    };

    if (result) {
        return (
            <div className="lot-import-panel">
                <h3 className="lot-import-title">Import Complete</h3>
                <p className="lot-import-summary">
                    <strong>{result.inserted}</strong> lot{result.inserted !== 1 ? 's' : ''} imported successfully.
                    {result.errors.length > 0 && (
                        <> <strong>{result.errors.length}</strong> row{result.errors.length !== 1 ? 's' : ''} had errors and were skipped.</>
                    )}
                </p>
                {result.errors.length > 0 && (
                    <ul className="lot-import-error-list">
                        {result.errors.map((e, i) => (
                            <li key={i}>Row {e.row}: {e.error}</li>
                        ))}
                    </ul>
                )}
                <div className="lot-form-actions">
                    <button className="search-button" onClick={onDone}>Done</button>
                </div>
            </div>
        );
    }

    return (
        <div className="lot-import-panel">
            <h3 className="lot-import-title">Import Preview</h3>
            <p className="lot-import-summary">
                {rows.length} row{rows.length !== 1 ? 's' : ''} found
                {unrecognised.length > 0 && (
                    <> &mdash; unrecognised columns ignored: <em>{unrecognised.join(', ')}</em></>
                )}.
                {errorCount > 0 && (
                    <> <strong className="lot-import-warn">{errorCount} row{errorCount !== 1 ? 's' : ''} have errors and will be skipped.</strong></>
                )}
            </p>

            <div className="lot-import-table-wrap">
                <table className="results-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Lot #</th>
                            <th>Title</th>
                            <th>Make</th>
                            <th>Model</th>
                            <th className="col-price">Year</th>
                            <th className="col-price">Price</th>
                            <th className="col-price">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayRows.map((row) => (
                            <tr key={row._rowIndex} className={row._error ? 'import-row-error' : ''}>
                                <td>{row._rowIndex}</td>
                                <td>{row.lot_number || '—'}</td>
                                <td>{row.title || row.description || '—'}</td>
                                <td>{row.make || '—'}</td>
                                <td>{row.model || '—'}</td>
                                <td className="col-price">{row.year || '—'}</td>
                                <td className="col-price">{row.sale_price != null ? row.sale_price : '—'}</td>
                                <td className="col-price">
                                    {row._error
                                        ? <span className="import-badge import-badge-error" title={row._error}>Error</span>
                                        : <span className="import-badge import-badge-ok">OK</span>
                                    }
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {rows.length > 10 && (
                <button className="lot-import-showall" onClick={() => setShowAll(v => !v)}>
                    {showAll ? 'Show fewer rows' : `Show all ${rows.length} rows`}
                </button>
            )}

            {importError && <div className="lot-form-error">{importError}</div>}

            <div className="lot-form-actions">
                <button
                    className="search-button"
                    onClick={handleImport}
                    disabled={importing || validCount === 0}
                >
                    {importing
                        ? 'Importing...'
                        : `Import ${validCount} lot${validCount !== 1 ? 's' : ''}${errorCount > 0 ? ` (${errorCount} skipped)` : ''}`
                    }
                </button>
                <button className="search-button auction-back-button" onClick={onCancel} disabled={importing}>
                    Cancel
                </button>
            </div>
        </div>
    );
}

// ------------------------------------------------------------
// Page
// ------------------------------------------------------------
export default function EditAuctionPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [project,      setProject]      = useState(null);
    const [loading,      setLoading]      = useState(true);
    const [loadError,    setLoadError]    = useState(null);
    const [isSaving,     setIsSaving]     = useState(false);
    const [saveError,    setSaveError]    = useState(null);
    const [importRows,   setImportRows]   = useState(null);
    const [unrecognised, setUnrecognised] = useState([]);
    const [parseError,   setParseError]   = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const fetchProject = async () => {
            try {
                const res  = await apiFetch(`/api/auctions/${id}`);
                const data = await res.json();
                if (!res.ok) {
                    setLoadError(data.error || 'Failed to load auction.');
                    return;
                }
                setProject(data.project);
            } catch {
                setLoadError('Could not reach the API.');
            } finally {
                setLoading(false);
            }
        };
        fetchProject();
    }, [id]);

    const handleDelete = async () => {
        if (!window.confirm('Delete this auction? This cannot be undone from the app.')) return;
        try {
            const res = await apiFetch(`/api/auctions/${id}`, { method: 'DELETE' });
            if (!res.ok) {
                const err = await res.json();
                alert(err.error || 'Failed to delete auction.');
                return;
            }
            navigate('/auctions');
        } catch {
            alert('Could not reach the API.');
        }
    };

    const handleSave = async (formData) => {
        setIsSaving(true);
        setSaveError(null);
        try {
            const res = await apiFetch(`/api/auctions/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            if (!res.ok) {
                const err = await res.json();
                setSaveError(err.error || 'Failed to save.');
                return;
            }
            navigate(`/auctions/${id}`);
        } catch {
            setSaveError('Could not reach the API.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (!file) return;

        setParseError(null);
        try {
            const rawRows = await parseExcelFile(file);
            if (!rawRows.length) {
                setParseError('The file appears to be empty.');
                return;
            }
            const { rows, unrecognised: unrecog } = mapHeaders(rawRows);
            const recognised = rows.length > 0
                ? Object.keys(rows[0]).filter(k => !k.startsWith('_')).length
                : 0;
            if (recognised === 0) {
                setParseError('No recognised column headers found. Please use the template.');
                return;
            }
            setUnrecognised(unrecog);
            setImportRows(rows);
        } catch {
            setParseError('Could not parse the file. Make sure it is a valid Excel or CSV file.');
        }
    };

    return (
        <div className="app-layout">
            <Header />
            <main className="lot-form-page">
                {loading    && <div className="results-status">Loading...</div>}
                {loadError  && <div className="results-status results-error">{loadError}</div>}
                {!loading && !loadError && project && (
                    <>
                        {/* Hidden file input — lives outside the form to avoid nesting issues */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />

                        {importRows ? (
                            <LotImportPanel
                                projectId={id}
                                rows={importRows}
                                unrecognised={unrecognised}
                                onDone={() => navigate(`/auctions/${id}`)}
                                onCancel={() => { setImportRows(null); setUnrecognised([]); }}
                            />
                        ) : (
                            <AuctionForm
                                pageTitle="Edit Auction"
                                initialValues={project}
                                onSave={handleSave}
                                isSaving={isSaving}
                                saveError={saveError}
                                onBack={() => navigate(-1)}
                                onDelete={handleDelete}
                                toolbar={
                                    <>
                                        <Link to={`/lots/new?projectId=${project.project_id}`} className="search-button lot-toolbar-btn">
                                            + Add Lot
                                        </Link>
                                        <button
                                            type="button"
                                            className="search-button auction-back-button lot-toolbar-btn"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            Import from Excel
                                        </button>
                                        <button
                                            type="button"
                                            className="search-button auction-back-button lot-toolbar-btn"
                                            onClick={downloadTemplate}
                                        >
                                            Download Template
                                        </button>
                                        {parseError && (
                                            <span className="lot-form-error" style={{ margin: 0 }}>{parseError}</span>
                                        )}
                                    </>
                                }
                            />
                        )}
                    </>
                )}
            </main>
        </div>
    );
}
